import { get } from 'lodash';

import { SLOT_FAMILY_SINGLE } from '../constants/matchGridStakes';
import { createCache } from './$global';
import { getMatchGridTemplateByName } from './matchGridTemplates';
import {
  createStakeSlotsByParamsSequencer,
  defaultStakeSlotsSequencer,
} from './stakeSequencers';

/**
 * @private
 */
const $cache = createCache('getGridLineSlotTypesConf');

// --

const unknownSlot = Object.freeze({ slotType: null });
const unknownSlotArray = Object.freeze([unknownSlot]);

/**
 * Get slot types configuration for a combination of given `sportId`, `lineId` and `stakeType`.
 *
 * @param {number} sportId
 * @param {number} lineId
 * @param {number | undefined} stakeType
 * @return {Array<{slotType: number, filter: object.<string, any>}>}
 */
export function getGridLineSlotTypesConf(sportId, lineId, stakeType) {
  const $gridSlotTypesConf = $cache('slot-types');

  if (!(sportId in $gridSlotTypesConf)) {
    $gridSlotTypesConf[sportId] = {};
  }

  if (!(lineId in $gridSlotTypesConf[sportId])) {
    $gridSlotTypesConf[sportId][lineId] = {};
  }

  if (stakeType === null) {
    return unknownSlotArray;
  }
  else if (!(stakeType in $gridSlotTypesConf[sportId][lineId])) {
    const SLOT_TYPES_CONF = getMatchGridTemplateByName('GRID_SLOT_TYPES_CONF');

    $gridSlotTypesConf[sportId][lineId][stakeType] = (
      get(SLOT_TYPES_CONF, [sportId, lineId, stakeType])
      || get(SLOT_TYPES_CONF, ['default', lineId, stakeType])
      || [stakeType]
    )
      .map((entry) => {
        switch (typeof entry) {
          case 'number':
            return { slotType: entry };

          case 'undefined':
            return unknownSlot;

          case 'object': // assumed `{ slotType: number, filter: object }`
            return entry;

          default:
            throw new Error(`unrecognized grid slot types conf entry ${entry}`);
        }
      });
  }

  return $gridSlotTypesConf[sportId][lineId][stakeType];
}

// --

/**
 * Get slot type family configuration.
 *
 * @param {number} slotType
 * @return {SLOT_FAMILY}
 */
export function getGridLineSlotFamilyConf(slotType) {
  const $slotFamilyConf = $cache('slot-family');

  if (!(slotType in $slotFamilyConf)) {
    const SLOT_FAMILY_CONF = getMatchGridTemplateByName('GRID_SLOT_FAMILY_CONF');

    $slotFamilyConf[slotType] = (
      Object.keys(SLOT_FAMILY_CONF).find((key) => SLOT_FAMILY_CONF[key].includes(slotType))
    )
      || SLOT_FAMILY_SINGLE;
  }

  return $slotFamilyConf[slotType];
}

// --

/**
 * Get slot type name configuration.
 *
 * @param {number} slotType
 * @return {{ru: string, en: string}}
 */
export function getGridLineSlotNameConf(slotType) {
  const SLOT_NAME_CONF = getMatchGridTemplateByName('GRID_SLOT_NAME_CONF');

  return SLOT_NAME_CONF[slotType];
}

/**
 * Get slot type sequencing configuration.
 *
 * @param {number} slotType
 * @param {StakeEntity|Immutable.Map<string, *>} stake – the first (any) stake entry
 * @return {{index: number, sequencer: function, params: String[]}}
 */
export function getGridLineSlotSequencingConf(slotType, stake) {
  const $slotSequencingConf = $cache('slot-sequencing');

  if (!(slotType in $slotSequencingConf)) {
    const SLOT_SEQUENCING_CONF = getMatchGridTemplateByName('GRID_SLOT_SEQUENCING_CONF');
    $slotSequencingConf[slotType] = (SLOT_SEQUENCING_CONF && SLOT_SEQUENCING_CONF[slotType]) || {};
  }

  const $conf = $slotSequencingConf[slotType];

  // Нужно для того, чтобы сортировать строки одинаковой длинны,
  // в независимости сколько знаков у родного индекса и получать при этом корректный результат.
  const indexLength = 20; // С запасом на будущее, потому что индексы ставок могут стать довольно длинными.

  if ((typeof $conf === 'number') || !$conf) {
    return {
      index: (
        $conf?.toString().padStart(indexLength, 0))
        || (slotType?.toString().padStart(indexLength, 0))
        || (stake.id.toString().padStart(indexLength, 0)),
      params: [],
      sequencer: defaultStakeSlotsSequencer,
    };
  }

  if (!('index' in $conf)) {
    $conf.index =
      slotType?.toString().padStart(indexLength, 0)
      || (stake.id.toString().padStart(indexLength, 0));
  }
  else {
    $conf.index =
      $conf.index?.toString().padStart(indexLength, 0)
      || (stake.id.toString().padStart(indexLength, 0));
  }

  if (!('sequencer' in $conf)) {
    $conf.sequencer = ('params' in $conf)
      ? createStakeSlotsByParamsSequencer($conf.params)
      : defaultStakeSlotsSequencer;
  }

  if (!('params' in $conf)) {
    $conf.params = [];
  }

  return $conf;
}
